import Core from '../../core/Core';
import { ExpireDefault } from '../../common/Constants';
import ExpirationManager from '../../common/ExpirationManager';
import RequestQueue from './RequestQueue';
import SendabilityPolicy, { SessionState } from './SendabilityPolicy';
import RetryPolicy from './RetryPolicy';
import FlushController from './FlushController';
import ExpirationRecovery from './ExpirationRecovery';
import RequestProcessor from './RequestProcessor';
/**
 * SessionsRequestHandler - Orchestrates request queuing and processing
 */
export default class SessionsRequestHandler {
    /**
     * Constructor
     * @param options - Optional configuration options
     */
    constructor(options) {
        this._core = Core.getInstance();
        this._coreStorage = this._core.getCoreStorage();
        // Initialize expiration manager
        this._expirationManager = new ExpirationManager(this._coreStorage.getSessionExpire() || ExpireDefault.DEFAULT_SESSION_TIMEOUT_MS, ExpireDefault.DEFAULT_MAX_DURATION_SESSION_MS, this._coreStorage);
        const maxWaitingRequests = options && typeof options.maxWaitingRequests === 'number' && options.maxWaitingRequests > 0
            ? options.maxWaitingRequests
            : 50;
        this._queue = new RequestQueue(maxWaitingRequests);
        const initialState = this._expirationManager.isStarted() ? SessionState.STARTED : SessionState.STOPPED;
        this._sendabilityPolicy = new SendabilityPolicy(initialState);
        // Disable application-layer retry - network layer handles all retries (25 attempts with exponential backoff)
        this._retryPolicy = new RetryPolicy(0, 0);
        this._flushController = new FlushController();
        this._expirationRecovery = new ExpirationRecovery(this._core, this._coreStorage, this._expirationManager);
        this._requestProcessor = new RequestProcessor(this._core, this._queue, this._sendabilityPolicy, this._retryPolicy, this._flushController, this._expirationRecovery);
        // Initialize listener arrays
        this.onRequestQueued = [];
        this.onWillSendRequest = [];
        this.onRequestSent = [];
        // Set listeners on processor
        this._requestProcessor.setListeners(this.onWillSendRequest, this.onRequestSent);
    }
    /**
     * Destroy the request handler
     */
    destroy() {
        this._expirationRecovery.getExpirationManager().reset();
        this._sendabilityPolicy.setState(SessionState.STOPPED);
        this._requestProcessor.destroy();
        this._queue.clear();
        this._flushController = new FlushController(); // Reset flush state
        // Clear event listeners
        this.onRequestQueued = [];
        this.onWillSendRequest = [];
        this.onRequestSent = [];
    }
    /**
     * Update the maximum waiting requests limit
     * @param maxWaitingRequests - New maximum limit
     */
    setMaxWaitingRequests(maxWaitingRequests) {
        this._queue.setMaxSize(maxWaitingRequests);
    }
    /**
     * Enqueue a request
     * @param request - Request to be enqueued
     * @returns true if queued, false if dropped
     */
    queueRequest(request) {
        const queued = this._queue.enqueue(request);
        if (queued) {
            // Notify listeners
            for (const listener of this.onRequestQueued) {
                listener(request);
            }
            // Start processing
            this._requestProcessor.processRequests();
        }
        return queued;
    }
    /**
     * Flush the request queue
     * @param timeoutMs - Timeout in milliseconds
     * @returns Promise that resolves when the request queue is flushed
     */
    flush(timeoutMs) {
        const flushPromise = this._flushController.startFlush(timeoutMs);
        // Wrap in try-catch to handle processing errors
        try {
            this._requestProcessor.processRequests();
        }
        catch (error) {
            this._flushController.fail('FLUSH_PROCESSING_ERROR', { originalError: error });
        }
        return flushPromise;
    }
    /**
     * Destroy the current session synchronously without firing SESSION_EXPIRE
     * and without auto-recovery. Drops any queued requests. After this call the
     * handler is still usable — the host app can start a new session right away.
     */
    destroySession() {
        this._requestProcessor.resetState();
        this._queue.clear();
        this._expirationRecovery.closeSession();
        this._sendabilityPolicy.setState(SessionState.STOPPED);
    }
    /**
     * Check if session has expired
     * @returns true if expired
     */
    hasExpired() {
        return this._expirationRecovery.hasExpired();
    }
    /**
     * Whether a session has been started at least once and its timestamps are
     * still persisted in storage (regardless of whether it has already expired).
     * Used by AppAnalytics.newSession() to detect the "fresh plugin instance
     * that finds a previous session persisted in localStorage" scenario.
     *
     * @returns true if `firstRequestTime` is present on the ExpirationManager
     */
    isSessionStarted() {
        return this._expirationManager.isStarted();
    }
    /**
     * Check whether the session is active (can queue events)
     * @returns true if active
     */
    isActive() {
        return this._queueableEvent();
    }
    /**
     * Check whether session can accept events
     * @returns true if queueable
     */
    _queueableEvent() {
        const { indexStart, indexStop } = this._getLastStartStopIndices();
        if (this._sendabilityPolicy.isStopped()) {
            return indexStart !== -1 && indexStart > indexStop;
        }
        else if (this._sendabilityPolicy.isStarted()) {
            return indexStart === -1 || (indexStart !== -1 && indexStart > indexStop);
        }
        return false;
    }
    /**
     * Get the last start and stop indices in the request array
     * @returns Object containing the last start and stop indices
     */
    _getLastStartStopIndices() {
        let indexStart = -1;
        let indexStop = -1;
        const requests = this._queue.getRequests();
        for (let i = requests.length - 1; i >= 0; i--) {
            const request = requests[i];
            const service = request.getService();
            if (service === 'session/start' && indexStart === -1) {
                indexStart = i;
            }
            else if (service === 'session/stop' && indexStop === -1) {
                indexStop = i;
            }
            if (indexStart !== -1 && indexStop !== -1) {
                break;
            }
        }
        return { indexStart, indexStop };
    }
    /**
     * Check if should send stats (for compatibility)
     * @returns true if session is started
     */
    shouldSendStats() {
        return this._sendabilityPolicy.isStarted();
    }
}
